import styled from '@emotion/styled';
import { Button, IconButton, Typography } from '@material-ui/core';
import CloudDownload from '@material-ui/icons/CloudDownload';
import Send from '@material-ui/icons/Send';
import { ClassAttributes, createElement, MouseEventHandler, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import DeliverableType from '../../../entity/DeliverableType';
import { Deliverable, File, UserOptimizer } from '../../../generated/graphql';
import { toJSDate } from '../../../graphql/datetime';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import HelpExpressDelivery from '../../page/ProjectEditingPage/components/DueScreen/HelpExpressDelivery';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';
import AspectRatioFixed from '../primitive/AspectRatioFixed';
import ButtonWithIcon from '../primitive/ButtonWithIcon';
import _FileSize from '../primitive/FileSize';
import ImageScreen from '../primitive/ImageScreen';
import LfToBr from '../primitive/LfToBr';
import PublicId from '../primitive/PublicId';
import AvatarAndName from './AvatarAndName';
import MediaPlayer from './MediaPlayer';
import ModificationRequestApproveDialog from './ModificationRequestApproveDialog';

export interface Props {
  creation: {
    assignee?: Pick<UserOptimizer, 'id' | 'name' | 'profileImageUrl'> | null;
  };
  deliverable: Pick<Deliverable, 'id' | 'intent' | 'createdAt'> & {
    creative: {
      file: Pick<File, 'mimeType' | 'url'>;
    };
    attachedThumbnail?: {
      file: Pick<File, 'size' | 'url'>;
    } | null;
  };
  modificationRequestApprovable: boolean;
  onClickDownloadButton: (e: any, type: DeliverableType) => void;
  onClickSendButton: MouseEventHandler<HTMLElement>;
  onClickApproveModificationRequestButton: () => void;
  className?: string;
  profileUrl: string;
}

const DeliverableDetail = ({
  creation,
  deliverable,
  modificationRequestApprovable,
  onClickDownloadButton,
  onClickSendButton,
  onClickApproveModificationRequestButton,
  className,
  profileUrl,
}: ClassAttributes<HTMLElement> & Props) => {
  const [isModificationRequestApproveDialogOpen, setModificationRequestApproveDialogOpen] = useState(false);

  const creativeDownloadButton = (
    <CreativeDownloadButton color="primary" onClick={(e) => onClickDownloadButton(e, 'creative')}>
      <StyledCloudDownloadIcon />
    </CreativeDownloadButton>
  );

  const sendButton = (
    <ButtonWithIcon icon={Send} onClick={onClickSendButton}>
      <FormattedMessage id="deliverable_detail.send" />
    </ButtonWithIcon>
  );

  const attachmentDownloadButton = (
    <IconButton color="primary" onClick={(e) => onClickDownloadButton(e, 'attachedThumbnail')}>
      <StyledCloudDownloadIcon />
    </IconButton>
  );

  const modificationRequestApproveButton = (
    <Button
      color="secondary"
      fullWidth
      onClick={() => {
        setModificationRequestApproveDialogOpen(true);
      }}
    >
      <FormattedMessage id="modification_request.approve" />
    </Button>
  );

  return (
    <Root className={className}>
      <VideoOrImage aspectRatio={[4, 3]}>
        <MediaPlayer mimeType={deliverable.creative.file.mimeType} url={deliverable.creative.file.url} />
      </VideoOrImage>

      <SendButtonWrapper>{sendButton}</SendButtonWrapper>

      <CreativeDownloadButtonWrap>
        {creativeDownloadButton}

        <Id variant="caption" color="textSecondary" noWrap>
          <FormattedDate
            value={toJSDate(deliverable.createdAt)}
            year="numeric"
            month="2-digit"
            day="2-digit"
            hour="2-digit"
            minute="2-digit"
          />
        </Id>
        <DeliverableId>
          <PublicId id={deliverable.id} variant="caption" color="textSecondary" />
        </DeliverableId>
      </CreativeDownloadButtonWrap>

      <ProfileLink variant="text" href={`${profileUrl}/users/${creation.assignee!.id}`} target="_blank">
        <AvatarAndName user={creation.assignee!} size={32} />
      </ProfileLink>

      <IntentHeading variant="h6" noWrap>
        <FormattedMessage id="deliverable_detail.intent_heading" />
      </IntentHeading>

      <Intent variant="subtitle1" component="p">
        <LfToBr text={deliverable.intent} />
      </Intent>

      {modificationRequestApprovable && (
        <ModificationRequestApproveButtonWrapper>
          {modificationRequestApproveButton}
          <HelpExpressDelivery messageId="help.modification_request.approve" />
        </ModificationRequestApproveButtonWrapper>
      )}

      {deliverable.attachedThumbnail && (
        <Attachment>
          <AttachmentHeading variant="h6">
            <FormattedMessage id="deliverable_detail.heading.attachment" />
          </AttachmentHeading>
          <AttachedThumbnail src={deliverable.attachedThumbnail.file.url} clickable />
          <AttatchmentType>
            <FormattedMessage id="deliverable_detail.attachment_type.thumbnail_image" />
          </AttatchmentType>
          <FileSize fileSize={deliverable.attachedThumbnail.file.size}>
            {(fileSize) => <Typography variant="caption">{fileSize}</Typography>}
          </FileSize>
          <AttachmentDownloadButtonWrapper>{attachmentDownloadButton}</AttachmentDownloadButtonWrapper>
        </Attachment>
      )}

      <ModificationRequestApproveDialog
        onClickAcceptButton={() => {
          setModificationRequestApproveDialogOpen(false);
          onClickApproveModificationRequestButton();
        }}
        onRequestClose={() => {
          setModificationRequestApproveDialogOpen(false);
        }}
        open={isModificationRequestApproveDialogOpen}
      />
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['auto', '2fr', 'auto', `${SPACING_UNIT * 3}px`, '40%'],
    rows: ['auto', 'auto', 'auto', '1fr', 'auto', 'auto'],
    areas: [
      ['video', 'video', 'video', '.', 'optimizer'],
      ['video', 'video', 'video', '.', 'intentHeading'],
      ['video', 'video', 'video', '.', 'intentBody'],
      ['sendButton', 'sendButton', 'creativeDownloadButton', '.', 'intentBody'],
      ['attachment', 'attachment', 'attachment', '.', 'intentBody'],
      ['.', '.', '.', '.', 'modificationRequestApproveButton'],
    ],
    justifyItems: 'flex-start',
  }),
  {
    [mq('sp')]: {
      ...gridLayoutContainer({
        columns: ['auto', '1fr', 'auto'],
        rows: ['auto', 'auto', 'auto', 'auto', 'auto', '1fr', 'auto'],
        areas: [
          ['optimizer', 'optimizer', 'optimizer'],
          ['video', 'video', 'video'],
          ['sendButton', 'sendButton', 'sendButton'],
          ['creativeDownloadButton', 'creativeDownloadButton', 'creativeDownloadButton'],
          ['attachment', 'attachment', 'attachment'],
          ['intentHeading', 'intentHeading', 'intentHeading'],
          ['intentBody', 'intentBody', 'intentBody'],
          ['modificationRequestApproveButton', 'modificationRequestApproveButton', 'modificationRequestApproveButton'],
        ],
        justifyItems: 'flex-start',
      }),
    },
  },
);

const ProfileLink = (styled(Button)(gridLayoutItem('optimizer'), {
  marginBottom: SPACING_REGULAR,
  padding: 0,
  textTransform: 'none',
}) as unknown) as typeof Button;

const Id = styled(Typography)({ paddingLeft: SPACING_UNIT * 1.5 });

const DeliverableId = styled(Typography)({
  marginLeft: SPACING_REGULAR,
});

const VideoOrImage = styled(AspectRatioFixed)(gridLayoutItem('video', { alignSelf: 'flex-start' }), {
  marginBottom: SPACING_REGULAR,
});

const CreativeDownloadButtonWrap = styled('div')(
  gridLayoutItem('creativeDownloadButton', { alignSelf: 'flex-start', justifySelf: 'flex-end' }),
  flexibleLayoutContainer({ alignItems: 'center' }),
  { marginLeft: SPACING_REGULAR, paddingTop: SPACING_UNIT },
  {
    [mq('sp')]: {
      margin: `0 0 ${SPACING_LARGE}px`,
    },
  },
);

const CreativeDownloadButton = styled(IconButton)({
  width: 24,
  height: 24,
});

const StyledCloudDownloadIcon = styled(CloudDownload)({
  position: 'absolute',
});

const SendButtonWrapper = styled('div')(
  gridLayoutItem('sendButton', { alignSelf: 'flex-start', justifySelf: 'flex-start' }),
  {
    height: 40,
  },
);

const IntentHeading = styled(Typography)(gridLayoutItem('intentHeading'), { marginBottom: SPACING_UNIT });

const Intent = (styled(Typography)(gridLayoutItem('intentBody'), {
  wordBreak: 'break-word',
  hyphens: 'auto',
  lineHeight: '1.6',
}) as unknown) as typeof Typography;

const Attachment = styled('div')(
  gridLayoutContainer({
    columns: ['auto', 'auto', '1fr'],
    rows: ['auto', '60px', '60px'],
    areas: [
      ['attachmentHeading', 'attachmentHeading', 'attachmentHeading'],
      ['attachedThumbnail', 'attachmentType', 'attachmentDownloadButton'],
      ['attachedThumbnail', 'attachmentFileSize', 'attachmentDownloadButton'],
    ],
  }),
  gridLayoutItem('attachment'),
  {
    marginTop: SPACING_UNIT * 4,
    alignContent: 'flex-start',
  },
  {
    [mq('sp')]: {
      margin: `0 0 ${SPACING_LARGE}px`,
    },
  },
);

const AttachmentHeading = styled(Typography)(gridLayoutItem('attachmentHeading'), {
  marginBottom: SPACING_REGULAR,
});

const AttachedThumbnail = styled(ImageScreen)(gridLayoutItem('attachedThumbnail', { alignSelf: 'center' }), {
  marginRight: SPACING_REGULAR,
  maxWidth: 200,
  height: 120,
});

const AttatchmentType = styled(Typography)(gridLayoutItem('attachmentType', { alignSelf: 'end' }), {
  whiteSpace: 'nowrap',
});
AttatchmentType.defaultProps = { color: 'textSecondary' };

const FileSize = styled(_FileSize)(gridLayoutItem('attachmentFileSize'));

const AttachmentDownloadButtonWrapper = styled('div')(
  gridLayoutItem('attachmentDownloadButton', { alignSelf: 'center' }),
  {
    marginLeft: SPACING_UNIT * 4,
    width: 24,
    height: 24,
  },
);

const ModificationRequestApproveButtonWrapper = styled('div')(
  gridLayoutContainer({
    columns: ['calc(100% - 30px)', `${SPACING_UNIT}px`, '22px'],
    rows: ['auto'],
    areas: [['modificationRequestApproveButton', 'auto', 'help']],
  }),
  gridLayoutItem('modificationRequestApproveButton', { alignSelf: 'center' }),
  {
    marginTop: SPACING_REGULAR,
    width: '100%',
  },
);

export default DeliverableDetail;
